/**
 * A configuração de riscos do componente comum de aplicação (#606): o que é um risco, como a API
 * aplica, edita e remove, e o que muda nos documentos. Usada pela tela de aplicação e pela ficha
 * do cargo.
 */
import { Chip, Stack, TextField, Typography } from '@mui/material';

import {
  contar,
  juntarNomes,
  linhaDeVoltaASeguir,
  mesmoId,
} from '../../components/applications/textosDoNivel';
import type {
  AcaoEmLote,
  ConfigDeAplicacao,
  ContextoDaRemocao,
  DestinoRef,
  JanelaDeDetalheProps,
  ResultadoDaChamada,
  ValorDeCampo,
} from '../../components/applications/tipos';
import { RiskApplicationModal } from '../../components/risk-applications/RiskApplicationModal';
import { EscolhaDeDocumentos, SeletorDeDocumentos } from '../../components/risk-applications/SeletorDeDocumentos';
import type { AplicacaoHerdada, Efetivos } from '../../services/efetivosService';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import { riskApplicationService } from '../../services/riskApplicationService';
import { REPORT_GROUPS, type ReportType } from '../../types/documentTypes.generated';
import { OccupationalRiskStatus, RiskType, type OccupationalRisk } from '../../types/occupationalRisk';
import {
  EXPOSURE_LEVEL_LABELS,
  ExposureLevel,
  type MeasurementInput,
  type RiskApplication,
  type RiskApplicationBulkPayload,
  type RiskApplicationCreatePayload,
  type RiskApplicationMeasurementRow,
} from '../../types/riskApplication';
import { hojeEmSaoPaulo } from '../../utils/hojeEmSaoPaulo';
import { ConferirAoMexer } from '../../components/applications/ConferirAoMexer';

export interface ValoresDoRisco {
  exposureLevel: ExposureLevel;
  /** Sempre vazio ao incluir: o risco sai em todos os documentos até alguém restringir. */
  reportTypes: ReportType[];
}

/** O lote aceita a marcação de documentos (E5, #606); o tipo compartilhado ainda não diz. */
type LoteComDocumentos = RiskApplicationBulkPayload & { reportTypes?: ReportType[] };

const TODOS_OS_DOCUMENTOS: ReportType[] = REPORT_GROUPS.flatMap((grupo) => grupo.types);

const PLURAL_DO_TIPO: Record<RiskType, string> = {
  [RiskType.PHYSICAL]: 'Físicos',
  [RiskType.CHEMICAL]: 'Químicos',
  [RiskType.BIOLOGICAL]: 'Biológicos',
  [RiskType.ERGONOMIC]: 'Ergonômicos',
  [RiskType.ACCIDENT]: 'Acidentes',
  [RiskType.OTHER]: 'Outros',
};

const ROTULO_DO_TIPO: Record<RiskType, string> = {
  [RiskType.PHYSICAL]: 'Físico',
  [RiskType.CHEMICAL]: 'Químico',
  [RiskType.BIOLOGICAL]: 'Biológico',
  [RiskType.ERGONOMIC]: 'Ergonômico',
  [RiskType.ACCIDENT]: 'Acidente',
  [RiskType.OTHER]: 'Outro',
};

const NIVEIS_DE_EXPOSICAO = Object.values(ExposureLevel);

export function documentosDe(aplicacao: RiskApplication): ReportType[] {
  return (aplicacao.reportTypes ?? []).map((vinculo) => vinculo.reportType);
}

function nomeDaAplicacao(aplicacao: RiskApplication): string {
  return aplicacao.risk?.riskFactorName || `Risco ${aplicacao.riskId}`;
}

// ============================================================================
// CAMPOS DA LINHA
// ============================================================================

interface SeletorDeExposicaoProps {
  valor: ExposureLevel | '';
  rotulo: string;
  aoMudar: (novo: ExposureLevel) => void;
  /** Opção vazia de partida, para quando nada vem escolhido (ação em lote). */
  vazio?: string;
}

function SeletorDeExposicao({ valor, rotulo, aoMudar, vazio }: SeletorDeExposicaoProps) {
  return (
    <TextField
      select
      size="small"
      value={valor}
      onChange={(evento) => aoMudar(evento.target.value as ExposureLevel)}
      SelectProps={{ native: true }}
      inputProps={{ 'aria-label': rotulo }}
      sx={{ minWidth: 140 }}
    >
      {vazio !== undefined && (
        <option value="" disabled>
          {vazio}
        </option>
      )}
      {NIVEIS_DE_EXPOSICAO.map((nivel) => (
        <option key={nivel} value={nivel}>
          {EXPOSURE_LEVEL_LABELS[nivel]}
        </option>
      ))}
    </TextField>
  );
}

// ============================================================================
// LINHA RECÉM-ADICIONADA (D7)
// ============================================================================

/** "confira": o valor veio sugerido, e ninguém deve aceitá-lo sem olhar. */
function Confira({ origem }: { origem: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
      <Chip size="small" color="warning" variant="outlined" label="confira" />
      <Typography variant="caption" color="text.secondary">
        {origem}
      </Typography>
    </Stack>
  );
}

/**
 * A exposição com que o risco entra: a que ele já tem de cima no destino; sem nenhuma, Médio. A
 * linha marca "confira" de qualquer jeito.
 */
function exposicaoInicial(riskId: string | number, efetivos: Efetivos<RiskApplication> | null): ExposureLevel {
  const deCima = efetivos?.herdadas.find((herdada) => !herdada.substituidaNoDestino && mesmoId(herdada.riskId, riskId));
  return deCima ? deCima.exposureLevel : ExposureLevel.MEDIUM;
}

// ============================================================================
// JANELA DE DETALHE
// ============================================================================

function DetalheDoRisco({ aberta, aplicacao, destino, aoFechar, aoSalvar }: JanelaDeDetalheProps<RiskApplication>) {
  return (
    <RiskApplicationModal
      open={aberta}
      onClose={aoFechar}
      onSaved={aoSalvar}
      scope={destino.nivel}
      scopeId={destino.id}
      mode="edit"
      applicationId={Number(aplicacao.id)}
      riskName={nomeDaAplicacao(aplicacao)}
    />
  );
}

// ============================================================================
// CONFIG
// ============================================================================

async function aplicarRiscos(destino: DestinoRef, riskIds: number[], valores: ValoresDoRisco): Promise<ResultadoDaChamada> {
  const lote: LoteComDocumentos = {
    riskIds,
    scope: destino.nivel,
    scopeId: destino.id,
    exposureLevel: valores.exposureLevel,
    startDate: hojeEmSaoPaulo(),
    reportTypes: valores.reportTypes,
  };
  const resposta = await riskApplicationService.bulkApply(lote);
  const jaExistiam = resposta.duplicates.map(Number);
  const aplicados = riskIds.filter(
    (id) => resposta.created.some((criada) => mesmoId(criada.riskId, id)) && !jaExistiam.includes(id)
  );
  return { aplicados, jaExistiam, falharam: [] };
}

function medicaoCopiada(medicao: RiskApplicationMeasurementRow): MeasurementInput {
  return {
    measurementEquipmentId: medicao.measurementEquipmentId ?? null,
    value: medicao.value === null ? null : Number(medicao.value),
    unit: medicao.unit ?? null,
    measuredAt: medicao.measuredAt.slice(0, 10),
    observations: medicao.observations ?? null,
    technique: medicao.technique ?? null,
    exposureTime: medicao.exposureTime ?? null,
    responsibleUserId: medicao.responsibleUserId ?? null,
    exceedsLimit: medicao.exceedsLimit ?? false,
    appliedToleranceLimit: medicao.appliedToleranceLimit ?? null,
    appliedToleranceLimitSource: medicao.appliedToleranceLimitSource ?? null,
    // O servidor recusa quartzo ausente em sílica e quartzo presente nos demais: só viaja o que a origem tem.
    ...(medicao.quartzPercent != null ? { quartzPercent: Number(medicao.quartzPercent) } : {}),
    assessmentType: medicao.assessmentType ?? null,
    comfortParameter: medicao.comfortParameter ?? null,
    measurementPoint: medicao.measurementPoint ?? null,
    storedQuantity: medicao.storedQuantity ?? null,
    storedQuantityUnit: medicao.storedQuantityUnit ?? null,
    transportedQuantity: medicao.transportedQuantity ?? null,
    transportedQuantityUnit: medicao.transportedQuantityUnit ?? null,
    riskArea: medicao.riskArea ?? null,
    storageType: medicao.storageType ?? null,
    hazardousFraming: medicao.hazardousFraming,
    hazardousAnnex: medicao.hazardousAnnex ?? null,
    unhealthyFraming: medicao.unhealthyFraming,
    unhealthyDegreeApplied: medicao.unhealthyDegreeApplied ?? null,
  };
}

/**
 * "Ajustar só neste <nível>": a própria nasce com tudo o que a de cima tem — exposição, datas,
 * observações, documentos, exames, medidas, EPIs e medições —, para o risco não sair dos próximos
 * documentos nem da convocação só por ter passado a ser editável aqui.
 */
function copiaDoHerdado(destino: DestinoRef, herdada: AplicacaoHerdada<RiskApplication>): RiskApplicationCreatePayload {
  const medidas = herdada.existingMeasures ?? [];
  const medidasDa = (categoria: 'ADMINISTRATIVE' | 'ENGINEERING') =>
    medidas.filter((medida) => medida.category === categoria).map((medida) => medida.measure);
  return {
    riskId: Number(herdada.riskId),
    scope: destino.nivel,
    scopeId: Number(destino.id),
    exposureLevel: herdada.exposureLevel,
    startDate: herdada.startDate ? herdada.startDate.slice(0, 10) : hojeEmSaoPaulo(),
    endDate: herdada.endDate ? herdada.endDate.slice(0, 10) : null,
    observations: herdada.observations ?? null,
    activityProcess: herdada.activityProcess ?? null,
    riskMatrixType: herdada.riskMatrixType ?? null,
    riskProbability: herdada.riskProbability ?? null,
    riskSeverity: herdada.riskSeverity ?? null,
    examTypes: (herdada.examTypes ?? []).map((linha) => linha.examType),
    reportTypes: documentosDe(herdada),
    existingAdministrativeMeasures: medidasDa('ADMINISTRATIVE'),
    existingEngineeringMeasures: medidasDa('ENGINEERING'),
    existingAdministrativeOther: herdada.existingAdministrativeOther ?? null,
    existingEngineeringOther: herdada.existingEngineeringOther ?? null,
    existingEpiIds: (herdada.existingEpis ?? []).map((linha) => Number(linha.epiId)),
    proposedAdministrative: herdada.proposedAdministrative ?? null,
    proposedEngineering: herdada.proposedEngineering ?? null,
    proposedEpi: herdada.proposedEpi ?? null,
    measurements: (herdada.measurements ?? []).map(medicaoCopiada),
  };
}

async function copiarRisco(destino: DestinoRef, herdada: AplicacaoHerdada<RiskApplication>): Promise<ResultadoDaChamada> {
  const copia = copiaDoHerdado(destino, herdada);
  await riskApplicationService.apply(copia);
  return { aplicados: [copia.riskId], jaExistiam: [], falharam: [] };
}

function eDocumento(valor: string): valor is ReportType {
  return TODOS_OS_DOCUMENTOS.some((tipo) => tipo === valor);
}

function documentosDoValor(valor: ValorDeCampo): ReportType[] {
  return Array.isArray(valor) ? valor.filter(eDocumento) : [];
}

/** Na ação em lote, "Sai em todos" é uma resposta, e não a falta dela: precisa de valor próprio. */
const SAI_EM_TODOS = 'todos';

/**
 * Ações em lote da aba "Já aplicados": o valor escolhido vai a cada risco marcado (D5). Definir
 * documentos SUBSTITUI a marcação de cada risco pela escolhida — somar faria de um risco sem
 * marcação, que sai em todos, um risco restrito sem ninguém ter pedido.
 */
const ACOES_EM_LOTE: AcaoEmLote<RiskApplication>[] = [
  {
    id: 'definir-documentos',
    rotulo: 'Definir documentos',
    campo: 'documentos',
    valorInicial: SAI_EM_TODOS,
    escolher: (valor, mudar) => (
      <EscolhaDeDocumentos
        soEstes={valor !== SAI_EM_TODOS}
        escolhidos={documentosDoValor(valor)}
        aoMudar={(soEstes, escolhidos) => mudar(soEstes ? escolhidos : SAI_EM_TODOS)}
      />
    ),
    valorPara: (_aplicacao, escolhido) => documentosDoValor(escolhido),
  },
  {
    id: 'mudar-exposicao',
    rotulo: 'Mudar exposição',
    campo: 'exposicao',
    valorInicial: null,
    escolher: (valor, mudar) => (
      <SeletorDeExposicao
        valor={NIVEIS_DE_EXPOSICAO.find((nivel) => nivel === valor) ?? ''}
        rotulo="Exposição para os marcados"
        vazio="Escolha o nível"
        aoMudar={mudar}
      />
    ),
  },
];

/** "Apaga 2 exames, 1 EPI e 1 medição": os números quando a aplicação os traz; sem eles, os tipos. */
function oQueSePerde(aplicacoes: RiskApplication[]): string {
  const listas = [
    { de: (a: RiskApplication) => a.examTypes, singular: 'exame', plural: 'exames' },
    { de: (a: RiskApplication) => a.existingEpis, singular: 'EPI', plural: 'EPIs' },
    { de: (a: RiskApplication) => a.existingMeasures, singular: 'medida', plural: 'medidas' },
    { de: (a: RiskApplication) => a.measurements, singular: 'medição', plural: 'medições' },
  ];
  const ligados = aplicacoes.length === 1 ? 'ligados a esta aplicação' : 'ligados a estas aplicações';
  const conhecidas = listas.every((lista) => aplicacoes.every((a) => lista.de(a) !== undefined));
  if (!conhecidas) return `Apaga os exames, EPIs, medidas e medições ${ligados}.`;
  const partes = listas
    .map((lista) => ({ ...lista, quantos: aplicacoes.reduce((soma, a) => soma + (lista.de(a)?.length ?? 0), 0) }))
    .filter((lista) => lista.quantos > 0)
    .map((lista) => contar(lista.quantos, lista.singular, lista.plural));
  return partes.length > 0
    ? `Apaga ${juntarNomes(partes)} ${ligados}.`
    : `Não há exames, EPIs, medidas nem medições ${ligados}.`;
}

function textoDeRemocao(aplicacoes: RiskApplication[], { destino, deCima }: ContextoDaRemocao<RiskApplication>): string[] {
  const umSo = aplicacoes.length === 1;
  return [
    oQueSePerde(aplicacoes),
    `Documentos já emitidos não mudam; os próximos saem sem ${umSo ? 'este risco' : 'estes riscos'}.`,
    ...deCima.map((herdada) =>
      linhaDeVoltaASeguir(nomeDaAplicacao(herdada), destino, herdada.origem, EXPOSURE_LEVEL_LABELS[herdada.exposureLevel])
    ),
  ];
}

export const CONFIG_DE_RISCOS: ConfigDeAplicacao<OccupationalRisk, RiskApplication, ValoresDoRisco> = {
  familia: 'risk',
  rotulos: {
    singular: 'risco',
    plural: 'riscos',
    titulo: 'Aplicação de Riscos Ocupacionais',
    daTela: 'Riscos',
    confira: 'Confira a exposição.',
  },

  destaque: {
    aceita: (aplicacao) =>
      aplicacao.exposureLevel === ExposureLevel.HIGH || aplicacao.exposureLevel === ExposureLevel.VERY_HIGH,
    rotulo: (quantos) => `${quantos} com exposição alta`,
  },

  carregarCatalogo: async () =>
    (await occupationalRiskService.getOccupationalRisks({ status: OccupationalRiskStatus.ACTIVE, limit: 2000 })).risks,
  item: {
    id: (risco) => risco.id,
    nome: (risco) => risco.riskFactorName,
    descricao: (risco) => risco.risk,
    perigo: (risco) => risco.hazard,
    tipo: (risco) => risco.riskType,
  },
  tipos: Object.values(RiskType).map((tipo) => ({ valor: tipo, rotulo: ROTULO_DO_TIPO[tipo], plural: PLURAL_DO_TIPO[tipo] })),

  aplicacao: {
    id: (aplicacao) => aplicacao.id,
    itemId: (aplicacao) => aplicacao.riskId,
    nome: nomeDaAplicacao,
    descricao: (aplicacao) => aplicacao.risk?.risk,
    tipo: (aplicacao) => aplicacao.risk?.riskType,
    periodo: (aplicacao) => aplicacao,
  },

  valoresIniciais: (risco, efetivos) => ({ exposureLevel: exposicaoInicial(risco.id, efetivos), reportTypes: [] }),
  chaveDoGrupo: (valores) => `${valores.exposureLevel}|${[...valores.reportTypes].sort().join(',')}`,
  aplicar: aplicarRiscos,
  copiarDoHerdado: copiarRisco,

  camposEditaveis: [
    {
      id: 'exposicao',
      rotulo: 'Exposição',
      ler: (aplicacao) => aplicacao.exposureLevel,
      render: (aplicacao, { gravar, somenteLeitura, recente }) =>
        somenteLeitura ? (
          <span>{EXPOSURE_LEVEL_LABELS[aplicacao.exposureLevel]}</span>
        ) : (
          <ConferirAoMexer sugerido={recente === true} selo={<Confira origem="veio sugerida" />}>
            <SeletorDeExposicao
              valor={aplicacao.exposureLevel}
              rotulo={`Exposição de ${nomeDaAplicacao(aplicacao)}`}
              aoMudar={gravar}
            />
          </ConferirAoMexer>
        ),
    },
    {
      id: 'documentos',
      rotulo: 'Documentos',
      ler: documentosDe,
      render: (aplicacao, { gravar, somenteLeitura, efetivos }) => (
        <SeletorDeDocumentos
          nomeDoRisco={nomeDaAplicacao(aplicacao)}
          marcados={documentosDe(aplicacao)}
          sugestao={efetivos?.marcacaoSugerida}
          somenteLeitura={somenteLeitura}
          aoSalvar={(tipos) => void gravar(tipos)}
        />
      ),
    },
  ],
  editar: async (_destino, aplicacao, campo, valor) => {
    const id = Number(aplicacao.id);
    if (campo === 'exposicao') {
      await riskApplicationService.update(id, { exposureLevel: valor as ExposureLevel });
    } else if (campo === 'documentos') {
      await riskApplicationService.update(id, { reportTypes: [...(valor as readonly ReportType[])] });
    }
  },
  acoesEmLote: ACOES_EM_LOTE,
  JanelaDeDetalhe: DetalheDoRisco,
  remover: async (_destino, aplicacoes) => {
    await Promise.all(aplicacoes.map((aplicacao) => riskApplicationService.remove(Number(aplicacao.id))));
  },
  textoDeRemocao,

  linkDaFicha: (destino) => (destino.nivel === 'occupation' ? `/occupations/${destino.id}` : null),
};
